import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Add, Edit, DeleteOutline, FilterAlt } from '@mui/icons-material';
import {
  listSieveNests,
  createSieveNest,
  deleteSieveNest,
} from '../../../services/sieveNestService';
import type { SieveNest } from '../../../types/sieveNest';
import { SieveNestEditDialog } from './SieveNestEditDialog';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../components/shared/ConfirmDialog';

export const SieveNestsPage = () => {
  const theme = useTheme();
  const [nests, setNests] = useState<SieveNest[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SieveNest | null>(null);
  const [creating, setCreating] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSieveNests();
      setNests(data);
    } catch (err) {
      console.error('Failed to load sieve nests', err);
      setSnackbar({ open: true, message: 'Failed to load sieve nests', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleCreate = async () => {
    const name = window.prompt('New sieve nest name:');
    if (!name?.trim()) return;
    try {
      await createSieveNest(name.trim());
      setSnackbar({ open: true, message: 'Sieve nest created', severity: 'success' });
      await refresh();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      setSnackbar({ open: true, message: e?.response?.data?.error || 'Failed to create nest', severity: 'error' });
    }
  };

  const handleDelete = (nest: SieveNest) => {
    setConfirmDlg({
      open: true,
      title: `Delete "${nest.NestName}"?`,
      message: nest.IsBuiltIn
        ? 'This is a built-in sieve nest. Deleting it removes it from the dropdown for all future PSD tests, but does not affect tests that already use it.'
        : 'This will remove the sieve nest. PSD tests that already reference it are unaffected.',
      confirmLabel: 'Delete',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          await deleteSieveNest(nest.SieveNestKey);
          setSnackbar({ open: true, message: 'Sieve nest deleted', severity: 'success' });
          await refresh();
        } catch (err: unknown) {
          const e = err as { response?: { status?: number; data?: { error?: string; message?: string } } };
          if (e?.response?.status === 409) {
            setSnackbar({
              open: true,
              message: e.response.data?.message || 'Cannot delete: nest is in use by active PSD tests.',
              severity: 'error',
            });
          } else {
            setSnackbar({ open: true, message: 'Failed to delete', severity: 'error' });
          }
        }
      },
    });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Sieve Sets</Typography>
          <Typography variant="caption" color="text.secondary">
            Catalog of sieve nests available for PSD tests. Edits here apply to every future test that selects the nest.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setCreating(true); handleCreate().finally(() => setCreating(false)); }}
          disabled={creating} sx={{ textTransform: 'none' }}>
          New Sieve Nest
        </Button>
      </Box>

      <Stack spacing={1.5}>
        {nests.map((nest) => (
          <Paper key={nest.SieveNestKey} variant="outlined" sx={{
            p: 2, borderRadius: 2,
            borderColor: alpha(theme.palette.divider, 0.5),
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{nest.NestName}</Typography>
                {nest.IsBuiltIn && (
                  <Chip label="built-in" size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                )}
                <Chip
                  icon={<FilterAlt sx={{ fontSize: 14 }} />}
                  label={`${nest.sizes.length} ${nest.sizes.length === 1 ? 'size' : 'sizes'}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 18 }}
                />
              </Box>
              {nest.Description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {nest.Description}
                </Typography>
              )}
              {nest.sizes.length > 0 && (
                <Typography variant="caption" sx={{
                  display: 'block', mt: 0.5, color: 'text.secondary',
                  fontFamily: 'monospace', fontSize: '0.72rem',
                }}>
                  {nest.sizes
                    .slice()
                    .sort((a, b) => a.SortOrder - b.SortOrder)
                    .map((s) => s.SieveDisplayName || s.SieveDesignation || `${s.SieveSizeMm}mm`)
                    .join(' · ')}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Edit nest + sizes">
                <IconButton size="small" onClick={() => setEditing(nest)}><Edit fontSize="small" /></IconButton>
              </Tooltip>
              <Tooltip title="Delete nest">
                <IconButton size="small" color="error" onClick={() => handleDelete(nest)}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        ))}
        {nests.length === 0 && (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderColor: 'divider' }}>
            <Typography color="text.secondary">No sieve nests yet. Click "New Sieve Nest" to create one.</Typography>
          </Paper>
        )}
      </Stack>

      {editing && (
        <SieveNestEditDialog
          nest={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
            setSnackbar({ open: true, message: 'Saved', severity: 'success' });
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 2000 : 4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
    </Box>
  );
};
